import { useCallback, useMemo, useState } from 'react';

import type { NewBuildingQuiz } from '@search/graphql-typings';
import { dataLayerPush } from '@search/vtbeco-frontend-core/domain/google/dataLayerPush';
import type {
    NewBuildingQuizBuilderFieldEvent,
    NewBuildingQuizBuilderStepEvent
} from '@search/vtbeco-frontend-core/domain/newbuilding/analytics/NewBuildingQuizBuilderEvent';

import { getInitialQuizResult, getStepItemFormattedResult, QuizStepItemResult, QuizStepResult } from './service';

const useQuizBuilderState = (quiz: NewBuildingQuiz) => {
    const sortedSteps = useMemo(() => quiz.steps.sort((step1, step2) => step1.order - step2.order), [ quiz.steps ]);
    const totalSteps = sortedSteps.length;

    const [ result, setResult ] = useState<Array<QuizStepResult>>(getInitialQuizResult(sortedSteps));
    const [ quizId ] = useState<string>(Math.random().toString(36).substring(2));

    const [ stepIndex, setStepIndex ] = useState<number>(0);

    const goToNextStep = useCallback(() => setStepIndex(s => s + 1), []);
    const goToPrevStep = useCallback(() => setStepIndex(s => s - 1), []);
    const goToLastStep = useCallback(() => setStepIndex(totalSteps), [ totalSteps ]);

    const currentStepNumber = useMemo(() => stepIndex + 1, [ stepIndex ]);

    const handleNextStep = useCallback(() => {
        const { main, extra } = result[stepIndex];

        const mainStepResult = getStepItemFormattedResult(main);
        const extraStepResult = getStepItemFormattedResult(extra);

        /* eslint-disable @typescript-eslint/naming-convention */
        mainStepResult && dataLayerPush({
            quiz_id: quizId,
            event: 'nb_quiz_field_filled',
            event_category: mainStepResult.label,
            event_action: mainStepResult.value || 'Empty'
        } as NewBuildingQuizBuilderFieldEvent);

        extraStepResult && dataLayerPush({
            quiz_id: quizId,
            event: 'nb_quiz_field_filled',
            event_category: extraStepResult.label,
            event_action: extraStepResult.value || 'Empty'
        } as NewBuildingQuizBuilderFieldEvent);

        dataLayerPush({
            quiz_id: quizId,
            event: 'nb_quiz_step_filled',
            event_action: currentStepNumber
        } as NewBuildingQuizBuilderStepEvent);
        /* eslint-enable @typescript-eslint/naming-convention */

        goToNextStep();
    }, [ currentStepNumber, goToNextStep, quizId, result, stepIndex ]);

    const updateStepResult = useCallback((value: { main: QuizStepItemResult } | { extra: QuizStepItemResult }) => {
        setResult(prev => prev.map((prevStepResult, idx) => {
            return idx === stepIndex ? ({
                ...prevStepResult,
                ...value
            }) : prevStepResult;
        }));
    }, [ stepIndex ]);

    return {
        stepIndex,
        currentStepNumber,
        sortedSteps,
        totalSteps,
        handleNextStep,
        goToPrevStep,
        goToLastStep,
        result,
        updateStepResult,
        quizId
    };
};

export default useQuizBuilderState;
